import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { type FC, useCallback, useEffect, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { Eye, PencilSquare, Trash } from 'react-bootstrap-icons';
import { config } from '../../../modules/config';
import { useAuth, useNotifications } from '../../../modules/hooks';
import type { LockRecord } from '../../../modules/interfaces';
import { Can } from '../../../modules/utils/can';
import { CanMulti } from '../../CanMulti';
import { DotDropDown } from '../../Customize/DropDown';
import { useLocks } from '../../Dashboards/LockManagement/useLocks';
import { ConfirmModal, type ModalState } from '../../Modals/Confirm';
import { LockModal, type LockModalMode } from '../../Modals/Lock';
import { DataTable } from '../DataTable';

const LOCK_SUBJECT = 'lock_management';

interface LockDotMenuProps {
  data?: LockRecord;
  onView: (record: LockRecord) => void;
  onEdit: (record: LockRecord) => void;
  onDelete: (record: LockRecord) => void;
}

const LockDotMenu: FC<LockDotMenuProps> = ({ data, onView, onEdit, onDelete }) => {
  if (!data) return null;
  return (
    <CanMulti actions={['update', 'delete']} subject={LOCK_SUBJECT}>
      <DotDropDown title="" drop="end">
        <Dropdown.Item onClick={() => onView(data)}>
          <Eye className="dropdown-icon me-1" />
          View
        </Dropdown.Item>
        <Can I="update" a={LOCK_SUBJECT}>
          <Dropdown.Item onClick={() => onEdit(data)}>
            <PencilSquare className="dropdown-icon me-1" />
            Edit
          </Dropdown.Item>
        </Can>
        <Can I="delete" a={LOCK_SUBJECT}>
          <Dropdown.Item onClick={() => onDelete(data)}>
            <Trash className="dropdown-icon me-1" />
            Delete
          </Dropdown.Item>
        </Can>
      </DotDropDown>
    </CanMulti>
  );
};

export const LocksTable: FC = () => {
  const { setSimpleToasts } = useNotifications()!;
  const { credentialsInfo } = useAuth()!;
  const locksService = useLocks();

  const [locks, setLocks] = useState<LockRecord[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<LockModalMode>('create');
  const [activeRecord, setActiveRecord] = useState<LockRecord>();
  const [confirmModal, setConfirmModal] = useState<ModalState>({
    show: false,
    title: '',
    body: '',
    handler: () => {},
  });

  const loadLocks = useCallback(async () => {
    if (!credentialsInfo?.token) return;
    try {
      setLocks(await locksService.list());
    } catch {
      setSimpleToasts({ type: 'danger', message: 'Failed to load locks', show: true });
    }
  }, [credentialsInfo?.token, locksService, setSimpleToasts]);

  useEffect(() => {
    loadLocks();
  }, [loadLocks]);

  const onAdd = useCallback(() => {
    setActiveRecord(undefined);
    setModalMode('create');
    setShowModal(true);
  }, []);

  const onView = useCallback((record: LockRecord) => {
    setActiveRecord(record);
    setModalMode('view');
    setShowModal(true);
  }, []);

  const onEdit = useCallback((record: LockRecord) => {
    setActiveRecord(record);
    setModalMode('edit');
    setShowModal(true);
  }, []);

  const onDelete = useCallback(
    (record: LockRecord) => {
      setConfirmModal({
        show: true,
        title: 'Delete this lock?',
        body: 'This action cannot be undone.',
        handler: async () => {
          setConfirmModal((prev) => ({ ...prev, show: false }));
          await locksService.remove(record.uuid);
          await loadLocks();
          setSimpleToasts({ type: 'success', message: 'Lock deleted', show: true });
        },
      });
    },
    [loadLocks, locksService, setSimpleToasts],
  );

  const [columns] = useState<ColDef<LockRecord>[]>([
    { field: 'name', headerName: 'Lock name', flex: 3 },
    { field: 'provider', headerName: 'Provider', flex: 2 },
    {
      headerName: 'Room',
      flex: 2,
      valueGetter: (params) => params.data?.room?.name ?? '—',
    },
    {
      headerName: 'Groups',
      flex: 1,
      valueGetter: (params) => params.data?.groups?.length ?? 0,
    },
    {
      headerName: 'ACTION',
      colId: 'action',
      cellClass: 'actionCol justify-content-end',
      flex: 1,
      cellRenderer: (params: ICellRendererParams<LockRecord>) => (
        <LockDotMenu
          data={params.data}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ),
    },
  ]);

  return (
    <>
      <div className="my-custom-table">
        <DataTable
          addButtonFunction={onAdd}
          addButtonText="Add lock"
          component={LOCK_SUBJECT}
          refreshHandler={loadLocks}
          changeState={0}
          rowData={locks}
          columnDefs={columns}
          pagination
          paginationPageSize={config.tables.balances.paginationSize}
        />
      </div>

      <LockModal
        show={showModal}
        mode={modalMode}
        data={activeRecord}
        handleClose={() => setShowModal(false)}
        onSaved={loadLocks}
      />

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        body={confirmModal.body}
        handleCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
        handleConfirm={() => confirmModal.handler?.()}
      />
    </>
  );
};
